import React from 'react';

import Tile from '../Tile';

import { isWeekend } from '../shared/dates';
import {
	formatDay as defaultFormatDay,
	formatLongDate as defaultFormatLongDate,
} from '../shared/dateFormatter';
import { getMonth, startOfDay, subDays, addDays } from 'date-fns';
import { getRTL } from '@micro-apps/env';
import { cn } from '@fshr/shared-react';

type DayProps = {
	classes?: string[];
	currentMonthIndex: number;
	formatDay?: typeof defaultFormatDay;
	formatLongDate?: typeof defaultFormatLongDate;
	isAvailable?: boolean;
	isRestricted?: boolean;
	isSelected?: boolean;
	formattedPrice?: string;
	isSameDaySelection?: boolean;
	baseClassName?: string;
	nightlyRatesExperiment?: boolean;
	isMobile?: boolean;
} & Omit<React.ComponentProps<typeof Tile>, 'children' | 'formatAbbr' | 'view'>;

export default function Day({
	classes = [],
	currentMonthIndex,
	formatDay = defaultFormatDay,
	formatLongDate = defaultFormatLongDate,
	onClick,
	isAvailable,
	isRestricted,
	isSelected,
	isSameDaySelection,
	formattedPrice,
	baseClassName,
	nightlyRatesExperiment,
	isMobile,
	...otherProps
}: DayProps) {
	const { date, locale, minDate, maxDate } = otherProps;
	const className = `${baseClassName}__month-view__days__day`;
	const isRTL = getRTL();

	// The nightly-rates restricted indicator replaces Tile's dashed border, which only
	// showed for restricted days within range. Mirror that exact range check here so the
	// circle has the same visibility as the border it replaces.
	const isBeforeMinDate =
		minDate &&
		Math.floor(startOfDay(minDate).getTime() / 1000) >
			Math.floor(startOfDay(date).getTime() / 1000);
	const isSameOrAfterMaxDate =
		maxDate &&
		Math.floor(startOfDay(maxDate).getTime() / 1000) <=
			Math.floor(startOfDay(date).getTime() / 1000);
	const isRestrictedInRange = isRestricted && !isBeforeMinDate && !isSameOrAfterMaxDate;

	const classesProps: string[] = [];

	if (classes) {
		classesProps.push(...classes);
	}

	if (className) {
		classesProps.push(className);
	}

	if (isWeekend(date, locale)) {
		classesProps.push(`${className}--weekend`);
	}

	if (getMonth(date) !== currentMonthIndex) {
		// When the experiment is ON the neighboring-month cap is squared. Each branch is a
		// complete literal so Tailwind's content scanner emits the variant-prefixed CSS rule.
		if (isRTL) {
			classesProps.push(
				nightlyRatesExperiment
					? `${className}--neighboringMonth invisible [&+button]:[&>.range-padding-right]:invisible [&+button]:[&>div.range-center]:rounded-none`
					: `${className}--neighboringMonth invisible [&+button]:[&>.range-padding-right]:invisible [&+button]:[&>div.range-center]:rounded-r-full`
			);
		} else {
			classesProps.push(
				nightlyRatesExperiment
					? `${className}--neighboringMonth invisible [&+button]:[&>.range-padding-left]:invisible [&+button]:[&>div.range-center]:rounded-none`
					: `${className}--neighboringMonth invisible [&+button]:[&>.range-padding-left]:invisible [&+button]:[&>div.range-center]:rounded-l-full`
			);
		}
	}
	if (getMonth(subDays(date, 1)) !== currentMonthIndex) {
		if (isRTL) {
			classesProps.push(
				nightlyRatesExperiment
					? '[&>div.range-center]:rounded-none [&>.range-padding-right]:invisible'
					: '[&>div.range-center]:rounded-r-full [&>.range-padding-right]:invisible'
			);
		} else {
			classesProps.push(
				nightlyRatesExperiment
					? '[&>div.range-center]:rounded-none [&>.range-padding-left]:invisible'
					: '[&>div.range-center]:rounded-l-full [&>.range-padding-left]:invisible'
			);
		}
	}
	if (getMonth(addDays(date, 1)) !== currentMonthIndex) {
		if (isRTL) {
			classesProps.push(
				nightlyRatesExperiment
					? '[&>div.range-center]:rounded-none [&>.range-padding-left]:invisible'
					: '[&>div.range-center]:rounded-l-full [&>.range-padding-left]:invisible'
			);
		} else {
			classesProps.push(
				nightlyRatesExperiment
					? '[&>div.range-center]:rounded-none [&>.range-padding-right]:invisible'
					: '[&>div.range-center]:rounded-r-full [&>.range-padding-right]:invisible'
			);
		}
	}

	return (
		<Tile
			onClick={onClick}
			isSelected={isSelected}
			isSameDaySelection={isSameDaySelection}
			isRestricted={isRestricted}
			isAvailable={isAvailable}
			nightlyRatesExperiment={nightlyRatesExperiment}
			isMobile={isMobile}
			{...otherProps}
			classes={classesProps}
			formatAbbr={formatLongDate}
		>
			<div
				className={cn('flex flex-col items-center w-full', {
					'justify-center':
						isSelected ||
						(!formattedPrice && !nightlyRatesExperiment) ||
						(nightlyRatesExperiment && !!formattedPrice) ||
						(nightlyRatesExperiment && isRestrictedInRange && !formattedPrice) ||
						(nightlyRatesExperiment && !formattedPrice && !isRestrictedInRange),
					'justify-start pt-[3px]': !isSelected && !!formattedPrice && !nightlyRatesExperiment,
					'pb-[2px]': !isSelected && nightlyRatesExperiment && !!formattedPrice,
					'pb-[4px]':
						!isSelected && nightlyRatesExperiment && isRestrictedInRange && !formattedPrice,
					'pb-[10px]':
						!isSelected && nightlyRatesExperiment && !formattedPrice && !isRestrictedInRange,
				})}
			>
				<span>{formatDay(locale, date)}</span>
				{formattedPrice && (
					<span
						className={cn(
							'normal-case text-[8px] font-medium tracking-[1px] leading-[0] h-[7px] py-[4px]',
							isSelected ? 'text-button-primary-inverse' : 'text-button-primary'
						)}
					>
						{formattedPrice}
					</span>
				)}
				{nightlyRatesExperiment && isRestrictedInRange && (
					<span
						className={cn(
							'rounded-full w-[6px] h-[6px] shrink-0',
							isSelected ? 'bg-neutral-500' : 'bg-neutral-700 group-hover:bg-neutral-900'
						)}
					/>
				)}
			</div>
		</Tile>
	);
}
